import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../../firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import toast, { Toaster } from 'react-hot-toast';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import correctSoundFile from '../../assets/correct.mp3';
import wrongSoundFile from '../../assets/wrong.mp3';
import Navbar from '../../components/Navbar';
import { useWindowSize } from '@uidotdev/usehooks';

export default function NumberMemory() {
  const navigate = useNavigate();
  const { width, height } = useWindowSize();
  const [sequence, setSequence] = useState('');
  const [showSequence, setShowSequence] = useState(false);
  const [input, setInput] = useState('');
  const [level, setLevel] = useState(1);
  const [playLimit, setPlayLimit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [todayPlays, setTodayPlays] = useState(0);

  const [chronos, setChronos] = useState(0);
  const [dailyPlays, setDailyPlays] = useState(0);
  const [maxPlays, setMaxPlays] = useState(25);
  const [shake, setShake] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [message, setMessage] = useState('');
  const [floatingBonus, setFloatingBonus] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const correctSound = useRef(new Audio(correctSoundFile));
  const wrongSound = useRef(new Audio(wrongSoundFile));

  function getChronosBonus(level) {
    if (level >= 15) return 4;
    if (level >= 10) return 3;
    if (level >= 5) return 2;
    return 1;
  }

  const updateLeaderboard = async (level) => {
    if (!auth.currentUser) return;
    const userRef = doc(db, 'leaderboard_master', auth.currentUser.uid);
    const snap = await getDoc(userRef);
    const today = new Date().toDateString();
    const userProfileRef = doc(db, 'users', auth.currentUser.uid);
    const userProfileSnap = await getDoc(userProfileRef);

    let playerName = 'BrainyPlayer';
    if (userProfileSnap.exists()) {
      const userData = userProfileSnap.data();
      if (userData.name) playerName = userData.name;
      else if (auth.currentUser.displayName) playerName = auth.currentUser.displayName;
    } else if (auth.currentUser.displayName) {
      playerName = auth.currentUser.displayName;
    }

    if (snap.exists()) {
      const data = snap.data();
      await setDoc(userRef, {
        name: playerName,
        chronos,
        bestNumberMemoryLevel: Math.max(data.bestNumberMemoryLevel || 0, level),
        bestFocusGameStreak: data.bestFocusGameStreak || 0,
        bestSynonymWords: data.bestSynonymWords || 0,
        lastPlayed: today,
      }, { merge: true });
    } else {
      await setDoc(userRef, {
        name: playerName,
        chronos,
        bestNumberMemoryLevel: level,
        bestFocusGameStreak: 0,
        bestSynonymWords: 0,
        lastPlayed: today,
      });
    }
  };

  // Auth-guarded initial load: only reads, no writes on refresh
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const userRef = doc(db, 'users', user.uid);
        const snap = await getDoc(userRef);
        if (!snap.exists()) {
          setLoading(false);
          return;
        }
        const data = snap.data();
        const today = new Date().toDateString();

        // Load chronos and today's plays
        setChronos(data.chronos || 0);
        const plays = (data.numberMemoryPlaysDate === today)
          ? (data.numberMemoryPlays || 0)
          : 0;
        setTodayPlays(plays);

        // Determine daily limit based on user type
        const userType = (data.accessType || '').toLowerCase();
        const limit = userType === 'pro' ? 200 : 25;
        setPlayLimit(limit);
        setMaxPlays(limit); // ensure maxPlays matches playLimit
        setLimitReached(plays >= limit);
      } catch (error) {
        console.error('Error loading NumberMemory data:', error);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (playing) {
      const seq = Array.from({ length: level }, () => Math.floor(Math.random() * 10)).join('');
      setSequence(seq);
      setShowSequence(true);
      setTimeout(() => setShowSequence(false), 1000 + level * 400);
    }
  }, [level, playing]);

  const startGame = () => {
    setLevel(1);
    setPlaying(true);
    setMessage('');
    setInput('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!playing) return;
    if (!showSequence && input === sequence && !limitReached) {
      correctSound.current.play();
      const nextLevel = level + 1;
      setLevel(nextLevel);
      setMessage('✅ Correct! Level up!');
      const bonus = getChronosBonus(level);
      setFloatingBonus(`+${bonus}`);
      setTimeout(() => setFloatingBonus(null), 1000);
      setInput('');
      if ([5, 10, 15].includes(nextLevel)) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }

      const userRef = doc(db, 'users', auth.currentUser.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) return;
      const data = snap.data();
      const todayDate = new Date().toDateString();
      const playsCount = (data.numberMemoryPlaysDate === todayDate ? data.numberMemoryPlays : 0) + 1;
      const newChronos = (data.chronos || 0) + bonus;
      await updateDoc(userRef, {
        chronos: newChronos,
        numberMemoryPlays: playsCount,
        numberMemoryPlaysDate: todayDate,
      });
      setChronos(newChronos);
      setDailyPlays(playsCount);
      if (playsCount >= maxPlays) {
        setLimitReached(true);
        setPlaying(false);
        toast.success("🎯 You've maxed out today's plays! Come back tomorrow.", {
          style: { background: '#facc15', color: '#1e293b', fontWeight: 'bold' },
        });
      } else {
        toast.success(`✅ Correct! +${bonus} Chronos`, {
          style: { background: '#bbf7d0', color: '#064e3b', fontWeight: 'bold' },
        });
      }
    } else {
      wrongSound.current.play();
      setShake(true);
      setTimeout(() => {
        setShake(false);
        setPlaying(false);
        updateLeaderboard(level);
      }, 500);
      setMessage(`❌ Wrong! Sequence was ${sequence}`);
            toast.error(`❌ Oops! The correct sequence was ${sequence}`, {
        style: { background: '#fecaca', color: '#7f1d1d', fontWeight: 'bold' },
      });
    }
  };

  const handlePlayAgain = () => {
    if (limitReached) return toast('✅ Amazing play today. Come back tomorrow to beat your record!');
    startGame();
  };

  if (loading) return <div>Loading...</div>;
  if (limitReached) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-yellow-100 to-purple-100 p-6">
<h1 className="text-3xl font-bold text-purple-800 mb-4">🔢 Number Memory</h1>
        {limitReached && <Confetti width={width} height={height} recycle={false} numberOfPieces={250} />}

You have reached your daily limit of {maxPlays} plays. Come back tomorrow!</div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-yellow-100 to-purple-100 p-6">
        {showConfetti && <Confetti width={width} height={height} />}
        <Toaster position="top-center" />
        {limitReached && <Confetti width={width} height={height} recycle={false} numberOfPieces={250} />}
        <h1 className="text-3xl font-bold text-purple-800 mb-4">🔢 Number Memory</h1>
        <p className="text-sm text-gray-600 mb-2">No. of Digits: {level} | Max Plays: {maxPlays}</p>
        {!playing && (
          <button onClick={startGame} className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 game-button">
            Start Game
          </button>
        )}
        <div className={shake ? 'shake' : ''}>
          <div className="relative">
            {floatingBonus && <div className="floating-bonus">{floatingBonus}</div>}
            {playing && (
              showSequence ? (
                <div className="text-4xl font-mono tracking-wider mb-4">{sequence}</div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    value={input}
                    onChange={e => setInput(e.target.value.replace(/\D/, ''))}
                    className="px-4 py-2 border rounded w-48 text-center text-xl"
                    type="number" inputMode="numeric" pattern="[0-9]*"
                    maxLength={sequence.length}
                    required
                    autoFocus
                  />
                  <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                    Submit
                  </button>
                </form>
              )
            )}
          </div>
        </div>
        {(limitReached || (!playing && message)) && (
          <>
            {message && <p className="mt-6 text-lg text-gray-700 text-center">{message}</p>}
            <button onClick={handlePlayAgain} className="mt-4 px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700">
              Play again          </button>
          </>
        )}
        <p className="mt-6 text-sm text-purple-700">Chronos: {chronos} ⏳</p>
<style jsx>{`
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25%, 75% { transform: translateX(-5px); }
    50% { transform: translateX(5px); }
  }
  .shake {
    animation: shake 0.5s ease-in-out;
  }

  .game-button {
    transition: transform 0.3s ease, filter 0.3s ease;
    font-size: 1.125rem;
    padding: 0.75rem 1.5rem;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
  }

  .game-button:hover {
    transform: scale(1.05);
    filter: brightness(1.1);
  }

  .game-button:active {
    transform: scale(0.98);
    filter: brightness(0.95);
  }

  @keyframes floatUp {
    0% {
      opacity: 1;
      transform: translateY(0);
    }
    100% {
      opacity: 0;
      transform: translateY(-50px);
    }
  }

  .floating-bonus {
    position: absolute;
    top: -2rem;
    left: 50%;
    transform: translateX(-50%);
    font-size: 2.25rem;
    font-weight: 700;
    color: #16a34a;
    animation: floatUp 1.2s ease-out forwards;
    text-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }
`}</style>

      </div>
    </>
  );
}
